<template>
    <div>
        <div v-if="!fetchingSpaceUsers" class="d-flex flex-wrap align-center">
            <SpaceInviteDialog v-if="spaceUsers.length">
                <template v-slot:activator>
                    <v-btn color="primary">
                        <v-icon left small>add</v-icon>
                        Invite
                    </v-btn>
                </template>
            </SpaceInviteDialog>
            <v-spacer />
            <v-text-field
                class="mx-2"
                v-show="spaceUsers.length"
                placeholder="Search for a user..."
                autofocus
                prepend-inner-icon="mdi-magnify "
                v-model="userSearch"
                solo
                flat
                hide-details
                dense
                clearable>
                <template v-slot:append>
                    <v-menu offset-y :close-on-content-click="false">
                        <template v-slot:activator="{ on, attrs }">
                            <v-btn v-bind="attrs" v-on="on" small class="mr-n2">
                                <v-icon left>mdi-tune-variant</v-icon>
                                <span>Filters</span>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <div v-for="(item, index) in Object.entries(userCheckboxBinders)" :key="index">
                                <v-sheet rounded class="py-2 px-4 my-1">
                                    <v-switch inset dense hide-details class="pa-0 ma-0" v-model="item[1].status">
                                        <template v-slot:label>
                                            <div class="d-flex align-center">
                                                <v-icon left small>{{ item[1].icon }}</v-icon>
                                                <span class="text-capitalize">{{ item[0] }}</span>
                                            </div>
                                        </template>
                                    </v-switch>
                                </v-sheet>
                            </div>
                        </v-list>
                    </v-menu>
                </template>
            </v-text-field>
            <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                    <v-btn v-on="on" @click="$store.dispatch('spaceStore/fetchSpaceUsers', $route.params.sid)" text icon>
                        <v-icon>mdi-refresh</v-icon>
                    </v-btn>
                </template>
                <span>refresh</span>
            </v-tooltip>
        </div>

        <div v-if="!fetchingSpaceUsers">
            <v-data-table
                v-if="spaceUsers.length"
                :headers="userHeaders"
                :items="filteredUsersByRole"
                item-key="uid"
                :items-per-page.sync="itemsPerPage"
                :footer-props="{ 'items-per-page-options': itemsPerPageOptions }"
                class="mt-4">
                <template v-slot:top="{ options, pagination, updateOptions }">
                    <div class="d-flex align-center">
                        <v-spacer></v-spacer>
                        <v-data-footer
                            :pagination="pagination"
                            :options="options"
                            @update:options="updateOptions"
                            :itemsPerPageOptions="itemsPerPageOptions"
                            items-per-page-text="$vuetify.dataTable.itemsPerPageText"
                            class="no-border" />
                    </div>
                </template>
                <template v-slot:[`item.type`]="{ item }">
                    <v-icon v-if="item.space_role === roleTypes.SPACE_ADMIN">mdi-shield-account-outline</v-icon>
                    <v-icon v-else>mdi-account-outline</v-icon>
                </template>
                <template v-slot:[`item.user`]="{ item }">
                    <div class="d-flex flex-column py-2">
                        <div class="font-weight-medium secondary--text">
                            {{ item.full_name }}
                            <v-chip x-small v-if="item.is_active" color="success">Active</v-chip>
                            <v-chip x-small v-else color="error">Inactive</v-chip>
                        </div>
                        <span class="caption mr-3">{{ item.email }}</span>
                    </div>
                </template>
                <template v-slot:[`item.roles`]="{ item }">
                    <v-chip-group column>
                        <v-chip outlined label small v-if="item.space_role === roleTypes.SPACE_ADMIN && userCheckboxBinders.administrators.status" class="pr-0">
                            <v-icon left small>mdi-shield-account-outline</v-icon>
                            <span class="text-truncate">{{ item.space_name }}</span>
                            <UserActions
                                :uid="item.uid.toString()"
                                :ucid="item.ucid.toString()"
                                :isActiveUser="item.is_active"
                                :userName="item.full_name"
                                :hierarchicalLevel="nuvolosObjectTypes.SPACE"
                                :showRevokeButton="true"
                                :showDeactivateActivateButton="false"
                                role="Space Administrator"
                                :spaceName="item.space_name"></UserActions>
                        </v-chip>
                        <template v-if="item.instance_editor_roles.length && userCheckboxBinders.editors.status">
                            <v-chip outlined label small v-for="role in item.instance_editor_roles" :key="role.iid" class="pr-0">
                                <v-icon left small>{{ instanceRole(role.role_name).icon }}</v-icon>
                                <span class="text-truncate">{{ role.Instance }}</span>
                                <UserActions
                                    :uid="item.uid.toString()"
                                    :ucid="item.ucid.toString()"
                                    :isActiveUser="item.is_active"
                                    :userName="item.full_name"
                                    :hierarchicalLevel="nuvolosObjectTypes.INSTANCE"
                                    :spaceAdmin="false"
                                    :showRevokeButton="true"
                                    :showDeactivateActivateButton="false"
                                    :instanceId="role.iid.toString()"
                                    :instanceName="role.Instance"
                                    :role="role.role_name"></UserActions>
                            </v-chip>
                        </template>
                        <template v-if="item.instance_viewer_roles.length && userCheckboxBinders.viewers.status">
                            <v-chip outlined label small v-for="role in item.instance_viewer_roles" :key="role.iid" class="pr-0">
                                <v-icon left small>{{ instanceRole(role.role_name).icon }}</v-icon>
                                <span class="text-truncate">{{ role.Instance }}</span>
                                <UserActions
                                    :uid="item.uid.toString()"
                                    :ucid="item.ucid.toString()"
                                    :isActiveUser="item.is_active"
                                    :userName="item.full_name"
                                    :hierarchicalLevel="nuvolosObjectTypes.INSTANCE"
                                    :spaceAdmin="false"
                                    :showRevokeButton="true"
                                    :showDeactivateActivateButton="false"
                                    :instanceId="role.iid.toString()"
                                    :instanceName="role.Instance"
                                    :role="role.role_name"></UserActions>
                            </v-chip>
                        </template>
                        <template v-if="item.instance_observer_roles.length && userCheckboxBinders.observers.status">
                            <v-chip outlined label small v-for="role in item.instance_observer_roles" :key="role.iid" class="pr-0">
                                <v-icon left small>{{ instanceRole(role.role_name).icon }}</v-icon>
                                <span class="text-truncate">{{ role.Instance }}</span>
                                <UserActions
                                    :uid="item.uid.toString()"
                                    :ucid="item.ucid.toString()"
                                    :isActiveUser="item.is_active"
                                    :userName="item.full_name"
                                    :hierarchicalLevel="nuvolosObjectTypes.INSTANCE"
                                    :spaceAdmin="false"
                                    :showRevokeButton="true"
                                    :showDeactivateActivateButton="false"
                                    :instanceId="role.iid.toString()"
                                    :instanceName="role.Instance"
                                    :role="role.role_name"></UserActions>
                            </v-chip>
                        </template>
                    </v-chip-group>
                    <AssignRolesDialog
                        :assignee="item.full_name"
                        :instanceData="getTargetInstancesForUserAssign(item.uid)"
                        :spaceName="item.space_name"
                        :ucid="item.ucid.toString()"
                        :spaceRole="item.space_role"></AssignRolesDialog>
                </template>
                <template v-slot:[`item.actions`]="{ item }">
                    <v-menu left offset-x nudge-top="10">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" text color="secondary" small>
                                <v-icon>more_horiz</v-icon>
                            </v-btn>
                        </template>
                        <v-list nav dense>
                            <UserActions
                                v-if="item.space_role !== roleTypes.SPACE_ADMIN"
                                :uid="item.uid.toString()"
                                :ucid="item.ucid.toString()"
                                :isActiveUser="item.is_active"
                                :userName="item.full_name"
                                :hierarchicalLevel="nuvolosObjectTypes.INSTANCE"
                                :spaceAdmin="false"
                                :showRevokeButton="false"
                                activatorType="listItem"></UserActions>
                            <UserActions
                                v-else
                                :uid="item.uid.toString()"
                                :ucid="item.ucid.toString()"
                                :isActiveUser="item.is_active"
                                :userName="item.full_name"
                                :hierarchicalLevel="nuvolosObjectTypes.SPACE"
                                :showRevokeButton="false"
                                activatorType="listItem"></UserActions>
                        </v-list>
                    </v-menu>
                </template>
                <template v-slot:no-data>
                    <span v-if="userSearch">No users found.</span>
                    <span v-else>No users found. Consider inviting new users via the "+ Invite" button above.</span>
                </template>
            </v-data-table>

            <div v-else>
                <v-alert text prominent type="info">
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold">No users found</span>
                        <span>
                            Currently this space has no users. You can
                            <SpaceInviteDialog>
                                <template v-slot:activator>
                                    <v-btn small dark color="info" outlined>
                                        <v-icon small>add</v-icon>
                                        Users
                                    </v-btn>
                                </template>
                            </SpaceInviteDialog>
                            new users.
                        </span>
                    </div>
                </v-alert>
            </div>
        </div>

        <div v-else>
            <v-skeleton-loader class="mx-auto" type="table"></v-skeleton-loader>
        </div>
    </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { enumsData } from '@/mixins/enums'
import { instanceRole } from '@/utils'
import SpaceInviteDialog from '@/modules/space/components/SpaceInviteDialog.vue'
const UserActions = () => import('@/components/UserActions')
const AssignRolesDialog = () => import('@/modules/space/components/TheSpaceAssignRolesDialog.vue')

export default {
    name: 'SpaceUsers',
    mixins: [enumsData],
    components: { UserActions, AssignRolesDialog, SpaceInviteDialog },
    data() {
        return {
            userSearch: '',
            itemsPerPage: 10,
            itemsPerPageOptions: [10, 25, 50, -1],
            userCheckboxBinders: {
                administrators: { status: true, icon: 'mdi-shield-account-outline' },
                editors: { status: true, icon: instanceRole('editor').icon },
                viewers: { status: true, icon: instanceRole('viewer').icon },
                observers: { status: true, icon: instanceRole('observer').icon }
            }
        }
    },
    computed: {
        ...mapState('spaceStore', ['spaceUsers', 'spaceInstances', 'fetchingSpaceUsers']),
        ...mapGetters('snapshotStore', ['isDevelopment']),
        userHeaders() {
            return [
                { text: 'Type', value: 'type', sortable: false, width: '40px' },
                { text: 'User', value: 'user', sortable: false, width: '320px' },
                { text: 'Roles by instance', value: 'roles', sortable: false },
                { text: 'Actions', value: 'actions', align: 'right', sortable: false, width: '120px' }
            ]
        },
        filteredUsersByRole() {
            return this.filteredSpaceUsers.filter(user => {
                if (
                    (this.userCheckboxBinders.administrators.status && user.space_role === this.roleTypes.SPACE_ADMIN) ||
                    (this.userCheckboxBinders.editors.status && user.instance_editor_roles && user.instance_editor_roles.length) ||
                    (this.userCheckboxBinders.viewers.status && user.instance_viewer_roles && user.instance_viewer_roles.length) ||
                    (this.userCheckboxBinders.observers.status && user.instance_observer_roles && user.instance_observer_roles.length)
                ) {
                    return user
                }
                return false
            })
        },
        filteredSpaceUsers() {
            if (!this.userSearch) {
                return this.spaceUsers
            } else {
                return this.spaceUsers.filter(user => {
                    const email = user.email.toLowerCase()
                    const name = user.full_name.toLowerCase()
                    return name.includes(this.userSearch.toLowerCase()) || email.includes(this.userSearch.toLowerCase())
                })
            }
        }
    },
    methods: {
        instanceRole,
        getTargetInstancesForUserAssign: function (uid) {
            const [spaceUserData] = this.spaceUsers.filter(user => user.uid === uid)
            const userAssignedInstances = spaceUserData.instance_editor_roles.concat(spaceUserData.instance_viewer_roles).reduce((roles, instance) => {
                roles[instance.iid] = instance.role_name
                return roles
            }, {})
            return this.spaceInstances.map(instance => ({
                iid: instance.iid,
                instance_name: instance.long_id,
                selectedRole: userAssignedInstances[instance.iid] || '',
                selectedInstance: null
            }))
        }
    },
    mounted() {
        this.$store.dispatch('spaceStore/fetchSpaceUsers', this.$route.params.sid)
    }
}
</script>
